import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Save } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Alert, AlertDescription } from '@/components/ui/alert';
import VisioMeter2 from '@/components/sop-intake/Visual-GON/VisioMeter2';
import { SettingsProvider as VGonSettingsProvider } from '@/components/sop-intake/Visual-GON/SettingsContext';
import type { ValueConfirm } from '@/components/sop-intake/Visual-GON/measurement-confirm';
import { getActiveTestData } from '@/components/sop-intake/constants';
import { useSettings } from '@/components/settings/lib/settings-context';
import { useClient } from '@/context/client-context';
import { useSopAnalyzer } from '@/hooks/use-sop-analyzer';
import type { RuleDocVersion } from '@/lib/posture-analysis/rule-params';
import { cn } from '@/lib/utils';
import { useRemoteShare } from './lib/remote-share-context';
import { useLivePose } from './lib/use-live-pose';
import SkeletonOverlay from './SkeletonOverlay';

/** Live landmarks over VGon's media pane while SOP Assessment mode is on screen and the toggle is on. */
function GoniometerLandmarks({ video, enabled }: { video: HTMLVideoElement | null; enabled: boolean }) {
  const landmarks = useLivePose(video, enabled);
  return <SkeletonOverlay video={video} landmarks={enabled ? landmarks : null} />;
}

type Selected = { testKey: string; side: 'L' | 'R' };

function fmt(value: unknown): string {
  return value === null || value === undefined || value === '' ? '—' : `${value}°`;
}

/**
 * Goniometer: VGon 3 itself, inline, measuring on the shared call window (its only source). The
 * right column keeps VGon's controls and adds the active SoP table and Save. Values save into an
 * SoP assessment locked to active mode; the workbench stays open and later saves update it.
 */
export default function RemoteGoniometerTool() {
  const { stream, tool, showLandmarks = true, reportSaveTarget } = useRemoteShare();
  const landmarksOn = showLandmarks && tool === 'goniometer' && !!stream;
  const { settings } = useSettings();
  const { client, setClient } = useClient();
  const ruleDocVersion: RuleDocVersion = settings.useLegacySopAssessment ? 'legacy' : 'current';
  const activeTestData = useMemo(() => getActiveTestData(ruleDocVersion), [ruleDocVersion]);
  const firstKey = Object.keys(activeTestData)[0];
  const [selected, setSelected] = useState<Selected | undefined>(firstKey ? { testKey: firstKey, side: 'L' } : undefined);
  const { formValues, handleInputChange, analysis, isAnalyzing, analysisError, handleSave, resetForm, saveStatus, isPersisted } =
    useSopAnalyzer(client, setClient, null, { navigateAfterCreate: false });
  // After the first save the assessment exists: further saves update it, and the button says so.
  // Remember what the last click did, so the first save confirms 'Saved', not 'Updated'.
  const [lastSaveWasUpdate, setLastSaveWasUpdate] = useState(false);

  // Name the record in the top bar: new until the first save, then the assessment being updated.
  const [savedAt, setSavedAt] = useState<Date | null>(null);
  useEffect(() => {
    if (isPersisted && !savedAt) setSavedAt(new Date());
    if (!isPersisted && savedAt) setSavedAt(null);
  }, [isPersisted, savedAt]);
  useEffect(() => {
    reportSaveTarget?.(
      'goniometer',
      isPersisted && savedAt
        ? `SOP Assessment (active), ${savedAt.toLocaleString(undefined, { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })}`
        : 'New SOP Assessment (active)',
    );
  }, [reportSaveTarget, isPersisted, savedAt]);

  // A fresh active-mode assessment for this session (no demo or leftover values).
  useEffect(() => {
    resetForm();
    handleInputChange('testMode', 'active');
  }, [resetForm, handleInputChange]);

  const handleConfirm: ValueConfirm = useCallback(
    (testKey, side, value, imageData, captureSource) => {
      const fieldId = `${side}_${testKey}`;
      handleInputChange(fieldId, String(value));
      if (imageData) handleInputChange(`${fieldId}_image`, imageData);
      handleInputChange(`${fieldId}_captureSource`, captureSource ?? 'screen-share');
    },
    [handleInputChange],
  );

  const canSave = !!analysis && !isAnalyzing && saveStatus !== 'saving';

  const sopPanel = (
    <Card className="shrink-0">
      <CardContent className="space-y-3 px-4 py-4">
        <p className="text-sm font-medium">Active SoP tests</p>
        <p className="text-xs text-muted-foreground">
          Ask the client to hold still. Freeze frame, line up the angle, Apply, then save.
        </p>
        <table className="w-full text-xs" aria-label="Active SoP tests">
          <thead>
            <tr className="text-muted-foreground">
              <th className="py-1 text-left font-normal">Test</th>
              <th className="py-1 text-right font-normal">L</th>
              <th className="py-1 text-right font-normal">R</th>
              <th className="py-1 text-right font-normal">Goal</th>
            </tr>
          </thead>
          <tbody>
            {Object.entries(activeTestData).map(([testKey, item]) => (
              <tr
                key={testKey}
                className={cn('cursor-pointer border-t border-border hover:bg-muted/50', selected?.testKey === testKey && 'bg-muted')}
                onClick={() => setSelected({ testKey, side: selected?.side ?? 'L' })}
              >
                <td className="py-1.5">{item.label}</td>
                <td className="py-1.5 text-right">{fmt(formValues[`L_${testKey}`])}</td>
                <td className="py-1.5 text-right">{fmt(formValues[`R_${testKey}`])}</td>
                <td className="py-1.5 text-right text-muted-foreground">{item.nominalDisplay ?? `${item.nominal}${item.unit ?? ''}`}</td>
              </tr>
            ))}
          </tbody>
        </table>
        {analysisError && (
          <Alert variant="destructive">
            <AlertDescription>{analysisError}</AlertDescription>
          </Alert>
        )}
        <Button className="w-full" onClick={() => {
            setLastSaveWasUpdate(isPersisted);
            void handleSave();
          }} disabled={!canSave}>
          <Save className="mr-2 h-4 w-4" />
          {saveStatus === 'saving'
            ? lastSaveWasUpdate ? 'Updating…' : 'Saving…'
            : saveStatus === 'saved'
              ? lastSaveWasUpdate ? 'Updated' : 'Saved'
              : isPersisted ? 'Update active assessment' : 'Save active assessment'}
        </Button>
      </CardContent>
    </Card>
  );

  return (
    <VGonSettingsProvider>
      <VisioMeter2
        embedded
        isOpen
        onClose={() => undefined}
        formValues={formValues}
        onValueConfirm={handleConfirm}
        initialTest={selected}
        testDataMap={activeTestData}
        clientName={client?.name}
        videoSourceStream={stream}
        sidebarExtra={sopPanel}
        renderMediaOverlay={(video) => <GoniometerLandmarks video={video} enabled={landmarksOn} />}
      />
    </VGonSettingsProvider>
  );
}
